/* eslint-disable max-len */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { TAB } from '@angular/cdk/keycodes';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyPaginator as MatPaginator, LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { MatDrawer } from '@angular/material/sidenav';
import { EMPTY, Observable, forkJoin, noop } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Contact, Country, Category, ContactPagination } from 'app/modules/contacts/contacts.types';
import { ContactSendMessageComponent } from 'app/modules/contacts/modals/send-message/send-message.component';
import { CompanyAndContactAddNoteDialogComponent } from 'app/modules/contacts/modals/add-note/add-note.component';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { Pipeline, Project, Property } from 'app/modules/projects/projects.types';
import { Account, Activity, FieldDef } from 'app/core/models/account.types';
import { User } from 'app/core/models/user.types';
import { Note } from 'app/shared/modules/notes/notes.types';
import { AccountService, AuthService } from 'app/core';
import { NotesService } from 'app/shared/modules/notes/notes.service';
import { FeatureFlag } from 'app/core/enums/feature-flag';
import { FeatureFlagService } from 'app/core/services/feature-flag.service';
import { AggregatedTasksService } from 'app/modules/aggregated-tasks/aggregated-tasks.service';
import { LoanQuote } from 'app/modules/projects/details/loans/loans.types';
import { LoansService } from 'app/modules/projects/details/loans/loans.service';
import { EMAIL_PATTERN } from 'app/shared/Utils/common.utils';
import { MergeContactComponent } from '../modals/merge-contact/merge-contact.component';
import { MergeEntityPayload } from 'app/modules/shared-resources/shared-resources.types';
import { Task } from 'app/shared/modules/legacy-tasks/tasks.types';
import { getAllowedFields, removeArchivedFieldsFromTableDefinition } from 'app/shared/functions';
import { RelatedToType } from 'app/shared/enums';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { EngagementsService } from 'app/modules/engagements/engagements.service';
import { AddEngagementDialogComponent } from 'app/modules/engagements/modals/add-engagement/add-engagement.component';
import { Engagement } from 'app/modules/engagements/engagements.types';
import { NewPermission, Permission } from 'app/core/enums';
import { SelectableFieldDef } from 'app/shared/components/column-selector/column-selector.types';
import { Dictionary } from 'app/shared/models';
import { InputOutput, LayoutService } from 'app/layout/layout.service';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { setupAddDealByDrawer } from 'app/modules/projects/@shared/functions.utils';
import { PipelineService } from 'app/modules/projects/pipeline.service';
import { AddDealParamsFormComponent } from 'app/modules/companies/modals/add-deal-params-form/add-deal-params-form.component';
import { ProjectTemplateService } from 'app/modules/projects/project-template.service';
import { CustomFormOptions } from 'app/shared/modules/custom-form/custom-form.types';
import { CustomFormComponent } from 'app/shared/modules/custom-form/custom-form/custom-form.component';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { PanelHostComponent } from 'app/shared/components/panels/panel-host.component';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { MatLegacyTabGroup as MatTabGroup } from '@angular/material/legacy-tabs';
import { FieldDefsState } from 'app/state/fields/state';
import { FieldMaskUtils } from '../../../shared/Utils/field-mask.utils';
import { TaskExportUtilsService } from '@tasks/services/task-export-utils.service';
import { UserState } from '@state/user/state';
import { AccountState } from '@state/account/state';
import { TaskActions } from '@tasks/state/actions';
import { TaskView } from '@tasks/tasks.types';
import { TaskState } from '@tasks/state/state';
import { TsSnackbarService } from 'tsui';
import { MentionsService } from '@shared/services/mentions.service';
import { PanelComponentBase } from '@shared/components/panels/panels.types';

const defaultAggregatedTaskTableFields: Partial<SelectableFieldDef>[] = [
  { id: -1, name: 'title', label: 'Title' },
  { id: -6, name: 'due_date', label: 'Due Date' },
  { id: -7, name: 'assigned_user_id', label: 'Assigned User' },
];

@Component({
  selector: 'contacts-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactsDetailsComponent extends OnDestroyMixin implements OnInit {
  editMode: boolean;
  foundTags: Dictionary<number>;
  contact: Contact;
  drawerOpened = false;
  contactForm: UntypedFormGroup = this._formBuilder.group({
    id: [''],
    name: ['', [Validators.required]],
    job_title: [''],
    company_name: [''],
    category_id: [''],
    email: ['', [Validators.email, Validators.pattern(EMAIL_PATTERN)]],
    phone: [''],
    mobile_phone: [''],
    description: [null],
    tag_list: [[]],
    remote_url: [''],
    associated_with_user_id: [''],
    address: this._formBuilder.group({
      address_string: '',
      street: '',
      city: '',
      state: '',
      postal_code: '',
      latitude: '',
      longitude: '',
    }),
  });
  Permission = Permission;
  contacts: Contact[];
  countries: Country[];
  categories: Category[];
  notes: Note[];
  engagements: Engagement[];
  calls: Engagement[];
  meetings: Engagement[];
  loanQuotes: LoanQuote[];
  tasks: Task[];
  account: Account;
  activities: Activity[];
  loanWorkflowStates: any[];
  workflowStates: any[];
  tagCtrl: UntypedFormControl = new UntypedFormControl();
  separatorKeysCodes: number[] = [TAB];
  isUploadingAvatarImage: boolean = false;
  customFieldDefs: FieldDef[] = [];
  allContactsFieldDefs: FieldDef[] = [];
  taskFieldDefs: FieldDef[];
  selectedFields: Partial<FieldDef>[];
  selectedOrder: string[];
  relatedPanelsAreLoading = true;
  tasks$: Observable<Task[]> = this.store.select(TaskState.getTasks);
  public drawerMode: 'over' | 'side';
  users: User[];
  public companyResults: any[] | null;
  private validAddress: string;
  public tableColumns: string[] = ['name', 'type', 'location'];
  public propertiesTableColumns: string[] = ['name', 'address'];
  public dataSource = new MatTableDataSource<Project>();
  public propertiesDataSource = new MatTableDataSource<Property>();
  public pagination: ContactPagination;
  public propertiesPagination: ContactPagination;
  public isPaginationLoading = false;
  public propertiesIsPaginationLoading = false;
  public data: Data;
  customObjects = [] as CustomObject[];
  staticTabCount: number;
  opened$: Observable<boolean>;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  protected readonly FieldMaskUtils = FieldMaskUtils;
  protected readonly NewPermission = NewPermission;
  protected readonly newPermissionsEnabled$ = this.store.select(
    AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS),
  );

  @ViewChild('matDrawer', { static: true }) matDrawer: MatDrawer;
  @ViewChild('avatarFileInput') private _avatarFileInput: ElementRef;
  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  @ViewChild('dealsPaginator') dealsPaginator: MatPaginator;
  @ViewChild('propertiesPaginator') propertiesPaginator: MatPaginator;
  @ViewChild(PanelHostComponent) panelHost: PanelHostComponent;
  @ViewChild('mainTabGroup', { static: true }) mainTabGroup: MatTabGroup;
  @ViewChildren('panels') panels: QueryList<PanelComponentBase<any>>;

  get hasLoanQuotesEnabled$(): Observable<boolean> {
    return this._featureFlagService.hasFeatureFlag(FeatureFlag.LOAN_QUOTES);
  }

  private get projectTemplates(): { id: number; name: string }[] {
    return this.data.options.project_templates
      .filter(({ hidden, related_to_type }) => !hidden && related_to_type === RelatedToType.Project)
      .sort((a, b) => a.rank - b.rank);
  }

  private get isCustomObjectRoute(): boolean {
    return this._router.url.includes('custom_object');
  }

  readonly mentionsService = inject(MentionsService);

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _contactsService: ContactsService,
    private readonly _notesService: NotesService,
    private readonly _engagementsService: EngagementsService,
    private readonly _loansService: LoansService,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _matDialog: MatDialog,
    private readonly _snackBar: TsSnackbarService,
    private readonly _router: Router,
    private readonly _accountService: AccountService,
    private readonly _aggregatedTasksService: AggregatedTasksService,
    private readonly _featureFlagService: FeatureFlagService,
    private readonly _confirmService: ConfirmService,
    private readonly authService: AuthService,
    private readonly layoutService: LayoutService,
    private readonly projectService: ProjectsService,
    private readonly propertiesService: PropertiesService,
    private readonly foldersTabService: FoldersTabService,
    private readonly pipelineService: PipelineService,
    private readonly projectTemplateService: ProjectTemplateService,
    private readonly store: Store,
    private readonly taskExportUtilsService: TaskExportUtilsService,
    private readonly actions: Actions,
  ) {
    super();

    this.account = this._accountService.getCurrentAccount();

    this._aggregatedTasksService.accountId = this.account.id;

    this.drawerMode = 'over';
  }

  get selectedFieldsTable(): FieldDef[] {
    return this.selectedOrder.map((fieldName: string) =>
      this.data.taskFieldDefs.find((taskFieldDef: FieldDef) => taskFieldDef.name === fieldName),
    );
  }

  fetchDetails(): void {
    this.account = this._accountService.getCurrentAccount();

    if (this.account.features.includes(FeatureFlag.CUSTOM_OBJECTS)) {
      const contactObjectDef = this.store
        .selectSnapshot(ObjectsManagerState.get)
        .find((x) => x.name === RelatedToType.Contact);

      this.customObjects = this.store.selectSnapshot(ObjectsManagerState.getLinkedObjects(contactObjectDef));
    }

    this.staticTabCount = this.adjustTabsCounter(6);

    if (this.isCustomObjectRoute) {
      const coId = this._activatedRoute.firstChild.firstChild.snapshot.params.id;
      const coTabIndex = this.customObjects.findIndex((x) => x.id === Number(coId));
      this.mainTabGroup.selectedIndex = this.staticTabCount + coTabIndex;
    }

    if (!this.contact.address) this.contact.address = {};

    this.selectedFields = defaultAggregatedTaskTableFields;
    this.buildForm();
    this.getAccountUsers();
    this.fetchContacts();
    this.fetchContactRelatedData();
    this.getAccountCategories();
    this.populateTagsOnType();
    this.getActivities();
    this.autoCompleteRelatedCompanies();
    this.fetchEngagements();
  }

  ngOnInit(): void {
    this.layoutService.emitCommon('crm-tasks', false, 'behaviorSubject');
    this.fetchFieldDefs();

    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.data = data;

      this.handleInvisibleFields(data.contact);

      this.contact = data.contact;
      this.store.dispatch(new TaskActions.SetRelatedTo(RelatedToType.Contact, +this.contact.id));
      this.store.dispatch(new TaskActions.SetTaskView(this.contact.task_list as TaskView));
      this.newFetchTasks();
      this.fetchDetails();
    });

    this.actions.pipe(ofActionCompleted(TaskActions.UpdateTask), untilComponentDestroyed(this)).subscribe(() => {
      this.newFetchTasks();
    });

    // MJR: This is a hack to force the task details to close properly
    this._router.events.pipe(untilComponentDestroyed(this)).subscribe((event) => {
      if (event instanceof NavigationEnd && !event.url.includes('task:')) {
        this.drawerOpened = false;
        this._changeDetectorRef.detectChanges();
      }
    });
    this.opened$ = this.layoutService.getCommon('crm-tasks').pipe(map((value) => value === true));
  }

  /**
   * Adjusts the tab counter value based on whether the user has email access or not.
   * We need to subtract 1 from the tab counter if user has FF 'email_engagements' access.
   * @param value - The initial value of the tab counter.
   * @returns The adjusted value of the tab counter after cross-checking with 'hasEmailAccess' method.
   */
  private adjustTabsCounter(value: number): number {
    return !this.hasEmailAccess() ? value : value - 1;
  }

  private buildForm(): void {
    this.contactForm.patchValue(this.contact);
  }

  private fetchFieldDefs(): void {
    const fields = this.store.selectSnapshot(FieldDefsState.getFieldDefs);
    const customFields = getAllowedFields(fields, RelatedToType.Contact);
    this.allContactsFieldDefs = customFields;
    this.customFieldDefs = customFields?.filter((field) => field.meta?.is_custom);
  }

  private getAccountUsers(): void {
    this._accountService.users$.pipe(untilComponentDestroyed(this)).subscribe((users: User[]) => {
      this.users = users;
      this._changeDetectorRef.markForCheck();
    });
  }

  private fetchContacts(): void {
    this._contactsService.contacts$.pipe(untilComponentDestroyed(this)).subscribe((contacts: Contact[]) => {
      this.contacts = contacts;
      this._changeDetectorRef.markForCheck();
    });
  }

  hasEmailAccess(): boolean {
    return this.account.features?.includes(FeatureFlag.EMAIL_ENGAGEMENTS) ?? false;
  }

  private fetchContactRelatedData(): void {
    const projects$ = this._contactsService.getProjects(this.contact.id);
    const properties$ = this._contactsService.getProperties(this.contact.id);
    const notes$ = this._notesService.getContactNotes(this.contact.id);
    const loanQuotes$ = this._contactsService.getLoanQuotes(this.contact.id);
    const loanStatuses$ = this._loansService.getLoanQuoteStatuses();

    this._aggregatedTasksService
      .getTaskStatuses()
      .pipe(untilComponentDestroyed(this))
      .subscribe((taskStatuses) => {
        this.workflowStates = taskStatuses;
      });

    forkJoin([projects$, properties$, notes$, loanQuotes$, loanStatuses$])
      .pipe(untilComponentDestroyed(this))
      .subscribe(([projects, properties, notes, loanQuotes, loanStatuses]) => {
        this.dataSource.data = projects.projects;
        this.pagination = projects.pagination;
        this.propertiesDataSource.data = properties.properties;
        this.propertiesPagination = properties.pagination;
        this.notes = notes;
        this.loanQuotes = loanQuotes?.filter(({ project }) => project);
        this.loanWorkflowStates = loanStatuses;
        this.toggleEditMode(false);
        this.relatedPanelsAreLoading = false;
        this._changeDetectorRef.detectChanges();
      });
  }

  private getAccountCategories(): void {
    // Get the country telephone codes
    this._accountService.categories$.pipe(untilComponentDestroyed(this)).subscribe((categories: Category[]) => {
      if (categories) {
        this.categories = categories.filter((option) => !option.hidden);
      }
      this._changeDetectorRef.detectChanges();
    });
  }

  private populateTagsOnType(): void {
    this.tagCtrl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        debounceTime(400),
        switchMap((value: string) => {
          return this._accountService.getAccountTags(RelatedToType.Contact, value).pipe(untilComponentDestroyed(this));
        }),
      )
      .subscribe((tags) => {
        this.foundTags = tags;
        this._changeDetectorRef.detectChanges();
      });
  }

  private autoCompleteRelatedCompanies(): void {
    this.contactForm
      .get('company_name')
      .valueChanges.pipe(untilComponentDestroyed(this), debounceTime(400), distinctUntilChanged())
      .subscribe((value) => {
        let query = value;
        if (!query.id) {
          this._accountService
            .typeaheadSearch(query, RelatedToType.Company)
            .pipe(untilComponentDestroyed(this))
            .subscribe((response) => {
              this.companyResults = response.companies;
              this._changeDetectorRef.markForCheck();
            });
        }
      });

    this.tasks$.pipe(untilComponentDestroyed(this)).subscribe((tasks) => {
      if (!tasks) {
        this.tasks = [];
        return;
      }
      this.tasks = [...tasks.filter((tsk) => tsk.task_type === 'task')];
      this.refreshSelectedFields();
      this._changeDetectorRef.markForCheck();
    });
  }

  public fetchEngagements(): void {
    this._engagementsService
      .getEngagementByContactId(parseInt(this.contact.id))
      .pipe(untilComponentDestroyed(this))
      .subscribe((data) => {
        if (data) {
          this.engagements = data?.engagements;
          this.parseEngagements();
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  createContactTask(): void {
    const task: Task = {
      task_type: 'task',
      task_list_id: this.contact.task_list.id,
      workflow_state: 'not_started',
    };

    this._contactsService
      .createTask(this.contact.id, this.contact.task_list.id, task)
      .pipe(untilComponentDestroyed(this))
      .subscribe((newTask) => {
        let route = this._activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        this.openTaskDetails(newTask);
        this.newFetchTasks();
        this.layoutService.emitCommon('crm-tasks', true);
      });
  }

  openTaskDetails(task: Task): void {
    const route = this._activatedRoute;
    this.drawerOpened = true;
    this.store.dispatch(new TaskActions.GetTaskDetail(task.task_list_id, task.id));
    this._router.navigate([{ outlets: { task: [task.task_list_id, task.id] } }], { relativeTo: route });
    this.layoutService.emitCommon('crm-tasks', true);
  }

  selectedIndexChange(index: number): void {
    if (index < this.staticTabCount) {
      if (this.isCustomObjectRoute) {
        this._router.navigate(['./'], { relativeTo: this._activatedRoute });
        window.dispatchEvent(new Event('resize'));
      }
    } else {
      const customObjectId = this.customObjects[index - this.staticTabCount].id;
      this._router.navigate(['/contacts', this.contact.id, 'custom_object', customObjectId], {
        relativeTo: this._activatedRoute,
        queryParams: { related_to_type: RelatedToType.Contact, related_to_id: this.contact.id },
      });
    }
  }

  private parseEngagements(): void {
    this.calls = this.engagements.filter((engagement) => engagement.engagement_type === 'Call');
    this.meetings = this.engagements.filter((engagement) => engagement.engagement_type === 'Meeting');
  }

  public onSaveColumns(selectedFieldDefs: SelectableFieldDef[]): void {
    this.selectedFields = selectedFieldDefs.length
      ? selectedFieldDefs
      : (defaultAggregatedTaskTableFields as SelectableFieldDef[]);
    this.selectedOrder = selectedFieldDefs.map(({ name }) => name) || [];

    // UrlUtils.saveToLocalStorage(LOCAL_STORAGE_KEYS.tableFields, this.selectedOrder, true);
    // this.savedView.definition = JSON.stringify({ table: { fields: this.selectedOrder } });
    // this.accountService.updateSavedView(this.savedView).subscribe();

    this.refreshSelectedFields();
  }

  private refreshSelectedFields(): void {
    // this.columnsConfig.dynamic = this.selectedFields;
    // this.columns = [...this.columnsConfig.prefix, ...this.columnsConfig.dynamic, ...this.columnsConfig.suffix];

    this.taskFieldDefs = [
      ...this.data.taskFieldDefs.map((taskFieldDef: FieldDef) => {
        const selected = !!this.selectedFields.find(
          (selectedField: SelectableFieldDef) => selectedField.name === taskFieldDef.name,
        );

        return {
          ...taskFieldDef,
          selected,
        };
      }),
    ];

    this.selectedOrder = this.selectedFields.map(({ name }) => name) || [];
  }

  /**
   * Check for new activities
   */
  private getActivities(): void {
    this._accountService
      .getActivities({
        account_id: this.account.id,
        per_page: 25,
        related_to_id: this.contact.id,
        related_to_type: RelatedToType.Contact,
      })
      .pipe(untilComponentDestroyed(this))
      .subscribe((activities) => {
        this.activities = activities;
        this._changeDetectorRef.detectChanges();
      });
  }

  setAddress(addressString: string): void {
    this.contactForm.get('address').get('address_string').setValue(addressString);
    this.validAddress = addressString;
  }

  onAddressBlur(): void {
    const addressString = this.contactForm.get('address').get('address_string').value;
    if (this.validAddress !== addressString) {
      this.contactForm.get('address').setValue({
        address_string: null,
        street: null,
        city: null,
        state: null,
        postal_code: null,
        latitude: null,
        longitude: null,
      });
    }
  }

  onPageChange(event: PageEvent, relatedToType: string): void {
    const pageIndex = event.pageIndex + 1;

    if (relatedToType === RelatedToType.Project) {
      this.isPaginationLoading = true;
      this._contactsService.getProjects(this.contact.id, pageIndex, event.pageSize).subscribe((result) => {
        this.dataSource.data = result.projects;
        this.pagination = result.pagination;
        this.isPaginationLoading = false;
        this._changeDetectorRef.detectChanges();
      });
    } else if (relatedToType === RelatedToType.Property) {
      this.propertiesIsPaginationLoading = true;
      this._contactsService.getProperties(this.contact.id, pageIndex, event.pageSize).subscribe((result) => {
        this.propertiesDataSource.data = result.properties;
        this.propertiesPagination = result.pagination;
        this.propertiesIsPaginationLoading = false;
        this._changeDetectorRef.detectChanges();
      });
    }
  }

  toggleEditMode(editMode: boolean | null = null): void {
    if (editMode === null) {
      this.editMode = !this.editMode;
    } else {
      this.editMode = editMode;
    }
    this._changeDetectorRef.markForCheck();
  }

  updateContact(): void {
    const contact = this.contactForm.getRawValue();
    this._contactsService
      .updateContact(contact)
      .pipe(untilComponentDestroyed(this))
      .subscribe((contact) => {
        this.contact = contact;
        this.contactForm.patchValue(contact);
        this.toggleEditMode(false);
        this.getActivities();
      });
  }

  deleteContact(): void {
    const id = this.contact.id;
    let nextContactId = null;

    if (this.contacts) {
      const currentContactIndex = this.contacts.findIndex((item) => item.id === id);
      const nextContactIndex = currentContactIndex + (currentContactIndex === this.contacts.length - 1 ? -1 : 1);
      nextContactId =
        this.contacts.length === 1 && this.contacts[0]?.id === id ? null : this.contacts[nextContactIndex]?.id;
    }

    this._contactsService
      .deleteContact(id)
      .pipe(untilComponentDestroyed(this))
      .subscribe({
        next: (isDeleted) => {
          if (!isDeleted) return;

          let route = this._activatedRoute;

          while (route.firstChild) route = route.firstChild;

          // Navigate to the next contact if available
          if (nextContactId) this._router.navigate(['../', nextContactId], { relativeTo: route });
          // Otherwise, navigate to the parent
          else this._router.navigate(['../'], { relativeTo: route });
        },
        error: (error: HttpErrorResponse) => {
          this._snackBar.open(`${error.error.contact?.errors}`, 'error');
        },
        complete: () => {
          this.toggleEditMode(false);
          this._changeDetectorRef.detectChanges();
        },
      });
  }

  uploadAvatar(fileList: FileList): void {
    if (!fileList.length) return;

    const allowedTypes = ['image/jpeg', 'image/png'];
    const file = fileList[0];

    if (!allowedTypes.includes(file.type)) return;

    this.isUploadingAvatarImage = true;

    this._contactsService
      .uploadAvatar(this.contact.id, file)
      .pipe(
        untilComponentDestroyed(this),
        catchError(() => {
          this._snackBar.open(`Unable to upload contact logo.`, 'error');
          return EMPTY;
        }),
        finalize(() => {
          this.isUploadingAvatarImage = false;
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe((res) => {
        this.contact.avatar_url = res.avatar_url;
        this._snackBar.open(`Contact logo successfully uploaded.`, 'success');
      });
  }

  removeAvatar(): void {
    this.isUploadingAvatarImage = true;

    this._contactsService
      .deleteAvatar(this.contact.id)
      .pipe(
        untilComponentDestroyed(this),
        catchError(() => {
          this._snackBar.open(`Unable to remove contact logo.`, 'error');
          return EMPTY;
        }),
        finalize(() => {
          this.isUploadingAvatarImage = false;
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(() => {
        if (this._avatarFileInput) this._avatarFileInput.nativeElement.value = null;
        this.contact.avatar_url = null;
        this._snackBar.open(`Contact logo successfully removed.`, 'success');
      });
  }

  addTag(event: MatChipInputEvent): void {
    const input = event.chipInput.inputElement;
    const value = event.value;

    if ((value || '').trim()) {
      this.contact.tag_list.push(value.trim());
      this.tagInput.nativeElement.focus();
    }

    if (input) {
      input.value = '';
    }

    this.tagCtrl.setValue(null);
    this.contactForm.get('tag_list').patchValue(this.contact.tag_list);
  }

  removeTag(tag: string): void {
    this.contact.tag_list.splice(
      this.contact.tag_list.findIndex((item) => item === tag),
      1,
    );
    this.contactForm.get('tag_list').patchValue(this.contact.tag_list);
    this._changeDetectorRef.markForCheck();
  }

  selectedTag(event: MatAutocompleteSelectedEvent): void {
    this.contact.tag_list.push(event.option.viewValue);
    this.tagInput.nativeElement.value = '';
    this.tagCtrl.setValue(null);
    this.contactForm.get('tag_list').patchValue(this.contact.tag_list);
  }

  openContactSendMessageModal(): void {
    const dialogRef = this._matDialog.open(ContactSendMessageComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { contact: this.contact },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.getActivities());
  }

  openContactAddNoteModal(): void {
    this._matDialog
      .open(CompanyAndContactAddNoteDialogComponent, {
        data: { contact: this.contact },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this._notesService
          .getContactNotes(this.contact.id)
          .pipe(untilComponentDestroyed(this))
          .subscribe((notes) => {
            this.notes = notes;
            this._changeDetectorRef.markForCheck();
          });
        this.getActivities();
      });
  }

  addContactEngagementModal(engagement_type: string): void {
    this._matDialog
      .open(AddEngagementDialogComponent, {
        data: {
          contact: this.contact,
          options: this.data.options,
          engagement_type: engagement_type,
          company: this.contact.company,
        },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.fetchEngagements();
        this.newFetchTasks();
        this.getActivities();
      });
  }

  editContactEngagementModal(engagement?: Engagement): void {
    this._matDialog
      .open(AddEngagementDialogComponent, {
        data: {
          contact: this.contact,
          options: this.data.options,
          engagement: engagement,
          company: this.contact.company,
        },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.fetchEngagements();
        this.newFetchTasks();
        this.getActivities();
      });
  }

  startNavigationTo(note: Note): void {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { noteable_id, noteable_type, pipeline_id } = note;

    switch (noteable_type) {
      case RelatedToType.Company:
        this._router.navigate(['/companies', noteable_id]);
        break;

      case RelatedToType.Contact:
        this._router.navigate(['/contacts', noteable_id]);
        break;

      case RelatedToType.Property:
        this._router.navigate(['/properties', noteable_id]);
        break;

      case RelatedToType.Project:
        this._router.navigate(['/projects', pipeline_id, noteable_id]);
        break;

      default:
        this._snackBar.open(
          `Unable to navigate to the selected ${noteable_type.toLowerCase()} at the moment. Try again later.`,
          'error',
        );
        break;
    }
  }

  openAddTaskDrawer() {
    this._router.navigateByUrl('/contacts/' + this.contact.id + '/task');
  }

  editNote(note: Note): void {
    this._matDialog
      .open(CompanyAndContactAddNoteDialogComponent, {
        data: { contact: note.noteable_type === RelatedToType.Contact ? this.contact : null, note: note },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((note: Note) => !!note),
        switchMap(() => this._notesService.getContactNotes(this.contact.id)),
      )
      .subscribe((notes) => {
        this.notes = notes;
        this._changeDetectorRef.markForCheck();
      });
  }

  deleteNote(note: Note): void {
    this._confirmService
      .openConfirmationModal()
      .pipe(
        untilComponentDestroyed(this),
        filter((res: boolean) => res),
        switchMap(() => this._notesService.deleteNote(note.id)),
        switchMap(() => this._notesService.getContactNotes(this.contact.id)),
        tap((notes) => {
          this.notes = notes;
          this._snackBar.open('Successfully deleted note.', 'success');
          this._changeDetectorRef.markForCheck();
        }),
      )
      .subscribe();
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  autocompleteCompanySelected(event): void {
    this.contactForm.patchValue(event.option.value, { emitEvent: false });
  }

  openMergeDialog(): void {
    this._matDialog
      .open(MergeContactComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { contact: this.contact },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((result?: MergeEntityPayload) => !!result),
        switchMap((result?: MergeEntityPayload) => this._contactsService.getContactById(result.destination_id)),
      )
      .subscribe(noop);
  }

  drawerClosed(): void {
    const route = this._activatedRoute;
    this._router.navigate([`../${this.contact.id}`], { relativeTo: route });
    this.layoutService.emitCommon('crm-tasks', false);
  }

  closeDrawer() {
    this.matDrawer.close();
  }

  customFieldUpdated(params: { [key: string]: any }): void {
    if (params) {
      this.contactForm.addControl(Object.keys(params)[0], new UntypedFormControl());
      this.contactForm.patchValue(params);
    }
  }

  hasAnyPermission(): boolean {
    return this.authService.hasAnyPermission(Permission.CONTACTS_MERGE, Permission.CONTACTS_DELETE);
  }

  addDeal(): void {
    this.layoutService.openComponentDrawer<InputOutput>(AddDealParamsFormComponent, {
      inputs: { pipelines: this.data.options.pipelines, showAddFieldOption: false },
      outputs: {
        close: (model: { pipeline_id: number; field_name?: string }) => {
          this.layoutService.closeComponentDrawer();

          if (!model) {
            return;
          }

          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { pipeline_id } = model;

          const pipeline: Pipeline = this.data.options.pipelines.find(({ id }) => id === pipeline_id);

          setTimeout(
            () =>
              setupAddDealByDrawer(
                {
                  projectService: this.projectService,
                  pipelineService: this.pipelineService,
                  foldersTabService: this.foldersTabService,
                  layoutService: this.layoutService,
                  router: this._router,
                  matDialog: this._matDialog,
                  authService: this.authService,
                },
                {
                  pipeline,
                  disableItemSuggestion: false,
                  parentProject: null,
                  projectTemplates: this.projectTemplates,
                  data: this.data,
                  account: this.account,
                  fieldDefs: getAllowedFields(this.data.fieldDefs, RelatedToType.Project),
                  extraDealData: {
                    deal_source: RelatedToType.Contact.toLowerCase(),
                    pipeline_id: pipeline.id,
                  },
                  createPortfolio: true,
                  successFn: (project: Project) => {
                    this.projectService
                      .createContactAssociation(this.contact, project.id)
                      .pipe(
                        untilComponentDestroyed(this),
                        tap(() => {
                          this.onPageChange(
                            {
                              pageIndex: 0,
                              pageSize: this.dealsPaginator?.pageSize ?? 10,
                              length: this.dealsPaginator?.length ?? 0,
                            },
                            RelatedToType.Project,
                          );
                          this.layoutService.closeComponentDrawer();
                        }),
                      )
                      .subscribe(noop);
                  },
                },
              ).subscribe(noop),
            500,
          );
        },
      },
    });
  }

  addProperty(): void {
    this.layoutService.openComponentDrawer<CustomFormOptions>(CustomFormComponent, {
      inputs: {
        relatedToType: RelatedToType.Property,
        renderAttachFileField: false,
        allowedFields: getAllowedFields(this.data.fieldDefs, RelatedToType.Property).filter(
          (field) => !field.meta.readonly && field.name !== 'project_ids' && field.name !== 'title',
        ),
        selectedFields: this.account.definitions?.property
          ? this.account.definitions.property.fields
          : [
              { name: 'name', isRequired: false, isRemovable: true },
              { name: 'property_type_id', isRequired: true, isRemovable: false },
              { name: 'template_id', isRequired: true, isRemovable: false },
              { name: 'cff_address', isRequired: false, isRemovable: true },
            ],
        dataSources: {
          template_id: this.data.options.project_templates
            .filter(({ hidden, related_to_type }) => !hidden && related_to_type === RelatedToType.Property)
            .sort((a, b) => a.rank - b.rank),
        },
        commonData: {
          formFieldData: this.data,
          disableItemSuggestion: true,
        },
        saveAction: (property: Property) => {
          this.propertiesService
            .saveProperty(property as Property)
            .pipe(
              untilComponentDestroyed(this),
              switchMap((newProperty) => {
                return this.propertiesService.createContactAssociation(this.contact, newProperty.id as number);
              }),
            )
            .subscribe({
              next: () => {
                this._snackBar.open('Property successfully created', 'success');
                this.onPageChange(
                  {
                    pageIndex: 0,
                    pageSize: this.propertiesPaginator?.pageSize ?? 10,
                    length: this.propertiesPaginator?.length ?? 0,
                  },
                  RelatedToType.Property,
                );
              },
              error: () => {
                this._snackBar.open('Unable to create a new property at this time. Please try again later.', 'error');
              },
              complete: () => {
                this.layoutService.closeComponentDrawer();
                this._changeDetectorRef.detectChanges();
              },
            });
        },
      },
      outputs: {
        onTemplateChanged: (fields) => {
          const definitions = {
            ...this.account.definitions,
            property: { fields },
          };
          this.updateCustomFormDefinition(definitions, () => this._changeDetectorRef.detectChanges());
        },
      },
    });
  }

  private updateCustomFormDefinition(definitions: any, subscribeFn?: () => void): void {
    this.account.definitions = definitions;

    this._accountService
      .updateAccount({
        id: this.account.id,
        configuration: {
          definitions: JSON.stringify(definitions),
        },
      })
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.account = this._accountService.getCurrentAccount();
        subscribeFn?.();
      });
  }

  onCustomObjectPanelDefUpdated(panelDef, tabDef): void {
    const currentTabId = tabDef.id;
    const tab = this.contact.template.definition.tabs.find(({ id }) => currentTabId === id);
    const tabLayout = tab.layout.find(({ panel: { id } }) => panelDef.id === id);
    tabLayout.panel = { ...panelDef };

    this.projectTemplateService
      .update({ ...this.contact.template, account_id: +this.account.id })
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  onCustomObjectPanelDataUpdated(newCustomObjectData: any): void {
    const contact = { ...this.contact, ...newCustomObjectData };
    this.contact = { ...contact };
    this._contactsService
      .updateContact(contact)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.getActivities();
        this.contactForm.patchValue(contact, { emitEvent: false });
        this.panels.forEach((panel) => {
          const notifiablePanels = ['form', 'matrix'];

          if (notifiablePanels.includes(panel.panelDef.type)) {
            panel.component.instance.completeSaving(false);
          }
        });
      });
  }

  onCustomObjectPanelEditToggled(data, tabDef) {
    const currentTabId = tabDef.id;
    const tab = this.contact.template.definition.tabs.find(({ id }) => currentTabId === id);
    const tabLayout = tab.layout.find(({ panel: { id } }) => data.panelDef.id === id);
    tabLayout.panel = { ...data.panelDef };
  }

  enableEditModeCustomObjectPanel(): void {
    this.panelHost.toggleEditMode();
  }

  openTaskExportDialog() {
    this.taskExportUtilsService
      .openTaskExportDialog()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        this.newDownloadTasks(result);
      });
  }

  public newFetchTasks(format: 'csv' | 'pdf' | undefined = undefined): void {
    const filter: any = {
      format: format,
      sort: [
        { field: 'due_date', order: 'desc' },
        { field: 'end_date', order: 'desc' },
      ],
    };

    this.store.dispatch(new TaskActions.FilterTasks(filter, true)).subscribe(() => {
      if (format) {
        this._snackBar.open('Your export is being processed and will be emailed to you shortly...');
      }
    });
  }

  public newDownloadTasks(format: 'csv' | 'pdf' = 'csv'): void {
    this.newFetchTasks(format);
  }

  canViewDeals(): boolean {
    return this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_VIEW));
  }

  canEditLayout(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.CONTACTS_MANAGE_LAYOUT));
    }

    return true;
  }

  canDelete(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.CONTACTS_DELETE));
    }

    return this.store.selectSnapshot(UserState.hasPermission(Permission.CONTACTS_DELETE));
  }

  handleInvisibleFields(contact: Contact): void {
    let arr = [];

    contact.template.definition.tabs[0].layout[0].panel.meta.fields.forEach((fieldArray) => {
      fieldArray = removeArchivedFieldsFromTableDefinition(fieldArray, this.allContactsFieldDefs);
      arr = [...arr, fieldArray];
    });

    contact.template.definition.tabs[0].layout[0].panel.meta.fields = this.hasFieldsFn(arr) ? arr : [];
  }

  hasFieldsFn(fields: any[][]): boolean {
    return (fields ?? []).reduce((acc, curr) => acc.concat(curr), []).length > 0;
  }
}
